import { Cookie } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Cookie className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
          <p className="text-gray-600">Last updated: November 28, 2024</p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <p className="text-gray-700 leading-relaxed">
              This Cookie Policy explains how DiaWise uses cookies and similar technologies to recognize you when you visit our application. It explains what these technologies are, why we use them, and your rights to control our use of them.
            </p>
          </CardContent>
        </Card>

        {/* What are cookies */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What are Cookies?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help websites remember your preferences and improve your user experience.
            </p>
          </CardContent>
        </Card>

        {/* Types of cookies */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Types of Cookies We Use</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">1. Essential Cookies</h3>
              <p className="text-gray-700 mb-2">
                These cookies are necessary for the website to function and cannot be switched off.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Authentication tokens</li>
                <li>Security cookies</li>
                <li>Session management</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">2. Functional Cookies</h3>
              <p className="text-gray-700 mb-2">
                These cookies enable enhanced functionality and personalization.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Language preferences</li>
                <li>Theme settings (light/dark mode)</li>
                <li>User interface preferences</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">3. Analytics Cookies</h3>
              <p className="text-gray-700 mb-2">
                These cookies help us understand how visitors interact with our application.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Usage statistics</li>
                <li>Feature usage tracking</li>
                <li>Performance monitoring</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* How we use cookies */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>How We Use Cookies</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-gray-700">
              <li>• To keep you signed in</li>
              <li>• To remember your preferences</li>
              <li>• To improve our services</li>
              <li>• To provide personalized content</li>
              <li>• To ensure security</li>
              <li>• To analyze usage patterns</li>
            </ul>
          </CardContent>
        </Card>

        {/* Your choices */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Your Choices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Browser Settings</h3>
              <p className="text-gray-700">
                Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies or delete certain cookies. However, if you disable cookies, some features of DiaWise may not function properly.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Opt-Out Links</h3>
              <p className="text-gray-700">
                You can opt out of analytics cookies while still allowing functional cookies through your app settings.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle>Questions?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              If you have questions about our use of cookies, please contact us at{' '}
              <a href="mailto:privacy@diawise.app" className="text-blue-600 hover:underline">
                privacy@diawise.app
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600">
          <p>© 2024 DiaWise. All rights reserved.</p>
          <p className="mt-2">
            <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>
            {' • '}
            <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
